import { EventEmitter } from "events";
import { PublicVenueSerializer } from "@app-contexts/dashboard/features/venues/data/types/venues";
import gtag from "@shared/lib/analytics/gtag";
import {
  NestedPublicVenueSerializer,
  PublicBaseEventSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import {
  AnalyticEventTypes,
  GtagTicketType,
  TrackTokenDataType,
} from "@shared/lib/analytics/types/analytics-types";
import { PublicTrackingTokensSerializer } from "@app-contexts/public/features/tracking-tokens/data/types";
import { triggerTokens } from "@shared/lib/analytics/trackingTokens";
import { enums, INVOICE_ITEM_TYPE_OPTIONS } from "@shared/constants";
import { TicketBasket } from "@shared/modules/basket/types";
import { VenueBasedInvoiceSerializer } from "@app-contexts/dashboard/shared/types/invoice";
import { PublicInvoiceDetailSerializer } from "@app-contexts/public/features/invoice-staging/data/types/invoice";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
// eslint-disable-next-line
import PublicTrackingTokenRepository from "@app-contexts/public/features/tracking-tokens/data/repositories/PublicTrackingTokenRepository";
import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import { checkFlagActive } from "@shared/contexts/WaffleContext/WaffleContext";
import { DjangoWaffleType } from "@shared/contexts/WaffleContext/django-waffle-type";
import { getFBOpts, retrieveVenue } from "./helpers";
import {
  IHasName,
  IHasTicketTypes,
  AnalyticsPageViewOptions,
  BeginCheckoutParams,
  AnalyticsPageViewData,
  EventTypes,
  AnalyticsViewContentData,
  AnalyticsButtonClickData,
} from "./AnalyticsService-types";
import { externalFormatter } from "./formatters";
import { ExternalAnalytics } from "./helpers/ExternalAnalytics";
import { InternalAnalytics } from "./helpers/InternalAnalytics";

export interface AnalyticsServiceClass extends EventEmitter {
  on(event: EventTypes.VenueInit, listener: () => void): this;
}

export class AnalyticsServiceClass extends EventEmitter {
  private isEnabled = true;
  private venue?: NestedPublicVenueSerializer | PublicVenueSerializer;
  private externalAnalytics = new ExternalAnalytics();
  private internalAnalytics = new InternalAnalytics();
  private trackingTokenRepo = new PublicTrackingTokenRepository();

  enable = () => {
    this.isEnabled = true;
  };

  disable = () => {
    this.isEnabled = false;
  };

  log = (name?: EventTypes, ...args: unknown[]) => {
    if (
      typeof window !== "undefined" &&
      window?.waffle?.FLAGS?.enable_analytics_debug_logging
    ) {
      // eslint-disable-next-line no-console
      console.log(`AnalyticsService::${name ?? ""}`, ...args);
    }
  };

  doIfEnabled = async (name: EventTypes, fn: () => Promise<void>) => {
    if (!this.isEnabled) return;

    this.emit(name);
    this.log(name);

    try {
      await fn();
    } catch (error) {
      logAppInfo(`Error sending analytics for ${name}`);
      recordAppError(error);
    }
  };

  initVenue = async (venue: typeof this.venue) => {
    this.log(EventTypes.VenueInit, venue?.id);

    if (this.isEnabled && venue && venue.id !== this.venue?.id) {
      // If the venue is changing, we need to disable the previous venue
      if (this.venue) {
        this.disableVenue(this.venue);
      }

      this.venue = venue;
      this.externalAnalytics.init(venue);
      this.emit(EventTypes.VenueInit, venue);
    }
  };

  private validateVenueId = async (venueId: number | undefined) => {
    if (!venueId) {
      this.log(undefined, "No venue id provided");
      return;
    }

    if (venueId !== this.venue?.id) {
      this.initVenue(await retrieveVenue(venueId));
    }
  };

  /*
   * Call this when a notable piece of content is viewed (eg: membership, event, product, etc).
   * This handles internal analytics and Facebook ViewContent only.
   * For Google view_item events, call viewItems() separately when ticket types are actually displayed.
   */
  viewContent = async ({ item, ...data }: AnalyticsViewContentData) => {
    return this.doIfEnabled(EventTypes.ViewContent, async () => {
      const uuid = await this.internalAnalytics.viewContent(data);

      await this.validateVenueId(data?.venue_id);

      // this will never happen because we validate the venue id in the validateVenueId function
      // but need it here because ts is a diva
      if (!this.venue || !item) {
        return;
      }

      // Always call external viewContent (Facebook, etc.) - no automatic Google view_item
      this.externalAnalytics.viewContent(item, uuid);
    });
  };

  eventLandingPageViewed = (
    event?: PublicBaseEventSerializer,
    data?: { custom_params?: Record<string, unknown> }
  ) => {
    if (!event) return;

    this.pageView({
      label: enums.INTERACTION_LABEL_MAP.EVENT_PAGE.id,
      event_id: event?.id,
      venue_id: event?.venue?.id,
      custom_params: data?.custom_params,
    });
  };

  eventViewed = (
    event?: PublicBaseEventSerializer,
    data?: AnalyticsViewContentData
  ) => {
    if (!event) return;

    this.viewContent({
      label: enums.INTERACTION_LABEL_MAP.EVENT_PAGE.id,
      item: { type: "ticket", ...event },
      event_id: event?.id,
      venue_id: event?.venue?.id,
      ...data,
      custom_params: {
        ...(data?.custom_params || {}),
        best_available_seating_enabled: checkFlagActive(
          window.waffle as DjangoWaffleType,
          "enable_best_assigned_seating",
          event?.venue?.id
        ),
      },
    });
  };

  websitePageViewed = (params: {
    websiteId: number;
    venueId: number;
    pageId: number;
  }) => {
    this.pageView({
      label: enums.INTERACTION_LABEL_MAP.WEBSITE_PAGE.id,
      venue_id: params.venueId,
      custom_params: {
        website_id: params.websiteId,
        page_id: params.pageId,
      },
    });
  };

  membershipLandingPageViewed = (
    membership?: PublicMembershipGroupSerializer,
    data?: { custom_params?: Record<string, unknown> }
  ) => {
    if (!membership) return;

    this.pageView({
      label: enums.INTERACTION_LABEL_MAP.MEMBERSHIP_PAGE.id,
      membership_id: membership?.id,
      venue_id: membership?.venue?.id,
      custom_params: data?.custom_params,
    });
  };

  membershipViewed = (
    membership?: PublicMembershipGroupSerializer,
    data?: AnalyticsViewContentData
  ) => {
    if (!membership) return;

    this.viewContent({
      label: enums.INTERACTION_LABEL_MAP.MEMBERSHIP_PAGE.id,
      item: { type: "membership", ...membership },
      membership_id: membership.id,
      venue_id: membership.venue?.id,
      ...data,
      custom_params: {
        ...(data?.custom_params || {}),
        best_available_seating_enabled: checkFlagActive(
          window.waffle as DjangoWaffleType,
          "enable_memberships_best_available_seating",
          membership?.venue?.id
        ),
      },
    });
  };

  productViewed = (
    product?: ProductTypeSerializer,
    data?: AnalyticsViewContentData
  ) => {
    if (!product) return;

    this.viewContent({
      label: enums.INTERACTION_LABEL_MAP.PRODUCT_PAGE.id,
      item: { type: "product", ...product },
      product_id: product.id,
      venue_id: product.venue,
      ...data,
    });
  };

  buttonClick = (data?: AnalyticsButtonClickData) => {
    if (!data) return undefined;

    return this.doIfEnabled(EventTypes.ButtonClick, async () => {
      await this.internalAnalytics.buttonClick(data);
    });
  };

  /**
   * ANALYTICS FIX: Decoupled viewItems from viewContent
   *
   * This method now handles ONLY view_item events for Google Analytics/GTM + internal analytics.
   * Previously was coupled with viewContent which caused duplicate/incorrect tracking.
   *
   * Key changes:
   * - Always calls internal analytics for proper funnel tracking
   * - Only calls Google Analytics/GTM (removed invalid Facebook ViewContent)
   * - Used when ticket types are actually displayed to user
   */
  viewItems = (
    ttset: GtagTicketType[],
    venue: NestedPublicVenueSerializer | PublicVenueSerializer
  ) => {
    return this.doIfEnabled(EventTypes.ViewItems, async () => {
      await this.validateVenueId(venue.id);

      // Always call internal analytics for view_item tracking
      await this.internalAnalytics.viewItems({
        label: enums.INTERACTION_LABEL_MAP.VIEW_ITEMS.id,
        venue_id: venue.id,
        event_id: ttset[0].event,
      });

      // Call external analytics (Google Analytics, GTM)
      this.externalAnalytics.viewItems(ttset, venue);
    });
  };

  pageView = async (
    data: AnalyticsPageViewData,
    opts: AnalyticsPageViewOptions = {}
  ) => {
    return this.doIfEnabled(EventTypes.PageView, async () => {
      await this.validateVenueId(data?.venue_id);

      const uuid = await this.internalAnalytics.pageView(data, opts?.routeUrl);

      // if there's no venue id we cant know which venue should get this event
      if (data?.venue_id) {
        this.externalAnalytics.pageView(getFBOpts(uuid, opts?.uuid));
      }
    });
  };

  addToCart = async (group: TicketItemGroupType, basket: TicketBasket) => {
    return this.doIfEnabled(EventTypes.AddToCart, async () => {
      await this.validateVenueId(group.venue_id);

      const uuid = await this.internalAnalytics.addToCart(group, basket);
      const formattedItemGroup = externalFormatter.formatItemGroup(group);
      const quantity = group.quantity ?? 0;

      // the fallback should never happen because it'll mean we didnt initalize the external analytics
      const opts = { uuid: uuid ?? "" };

      this.externalAnalytics.addToCart(
        formattedItemGroup,
        quantity,
        this.venue ?? { id: group.venue_id },
        opts
      );
    });
  };

  cartUpdated = async (basketResult: TicketBasket | undefined) => {
    return this.doIfEnabled(EventTypes.CartUpdated, async () => {
      await this.validateVenueId(basketResult?.payment_venue);

      const itemGroups = basketResult?.item_groups ?? [];
      const basket = basketResult ?? {};

      await Promise.all(
        itemGroups.map(async (group) => {
          return this.addToCart(group, basket);
        })
      );
    });
  };

  removeFromCart = () => {
    return this.doIfEnabled(EventTypes.RemoveFromCart, async () => {
      this.externalAnalytics.removeFromCart();
    });
  };

  // The strategy today is that we call this on every checkout step and other
  // interactions in the checkout. Our reports rely on this duplication
  // to determine how long a user spend in a given step. On the first step we call it with triggerExternal = true
  // so that we dont just track internally.
  beginCheckout = async (params: BeginCheckoutParams) => {
    return this.doIfEnabled(EventTypes.BeginCheckout, async () => {
      await this.validateVenueId(params.basket.paymentVenue);

      const uuid = await this.internalAnalytics.initiateCheckout(params);

      if (params.triggerExternal) {
        this.externalAnalytics.beginCheckout(
          externalFormatter.ibasketToGtagBasket(params.basket),
          this.venue,
          uuid
        );
      }
    });
  };

  postPurchase = async (
    finalBasket: TicketBasket,
    invoice: VenueBasedInvoiceSerializer | PublicInvoiceDetailSerializer | null,
    customParams?: Record<string, unknown>
  ) => {
    return this.doIfEnabled(EventTypes.PostPurchase, async () => {
      await this.validateVenueId(finalBasket.payment_venue);

      const uuid = await UserInteractionLogger.purchaseCart(
        finalBasket,
        invoice as PublicInvoiceDetailSerializer,
        customParams
      );

      this.externalAnalytics.postPurchase(
        externalFormatter.ticketBasketToGtagBasket(finalBasket),
        invoice?.venue,
        uuid
      );

      this.triggerPostPurchaseTrackingTokens(finalBasket, invoice);
    });
  };

  awConversion = (
    conversionID: string,
    conversionLabel: string,
    item: {
      value: number;
      currency: string;
      transaction_id: string;
    },
    ...args: unknown[]
  ) => {
    return this.doIfEnabled(EventTypes.AwConversion, async () => {
      this.externalAnalytics.awConversion(
        conversionID,
        conversionLabel,
        item,
        ...args
      );
    });
  };

  trackToken = (
    location: (typeof enums.TRACKING_TOKEN_LOCATION_MAP)[keyof typeof enums.TRACKING_TOKEN_LOCATION_MAP]["id"],
    venueID: NestedPublicVenueSerializer["id"],
    eventIDs: number[],
    data?: TrackTokenDataType
  ) => {
    return this.doIfEnabled(EventTypes.TrackToken, async () => {
      if (typeof navigator === "undefined" || !navigator?.sendBeacon) {
        return undefined;
      }

      const requestPromises = [
        // Non-event specific
        this.trackingTokenRepo.getPublicTrackingTokens({
          location,
          venue: venueID,
          event__in: null,
          event__isnull: true,
        }),
      ];

      // Event specific. These are only included if event__in is valid
      if (eventIDs && eventIDs.length) {
        requestPromises.push(
          this.trackingTokenRepo.getPublicTrackingTokens({
            location,
            venue: venueID,
            event__in: eventIDs.filter((id) => !!id).join(","),
          })
        );
      }

      return Promise.allSettled(requestPromises)
        .then((results) => {
          const venueWideTokens = results[0];
          const eventSpecificTokens = results[1];

          let allTokens: PublicTrackingTokensSerializer = [];

          if (venueWideTokens?.status === "fulfilled") {
            allTokens = allTokens.concat(venueWideTokens.value);
          }
          if (eventSpecificTokens?.status === "fulfilled") {
            allTokens = allTokens.concat(eventSpecificTokens.value);
          }

          return allTokens;
        })
        .then((allTokens) => {
          return triggerTokens(allTokens, data ?? {});
        })
        .catch((err) => {
          // If an error is thrown, catch it and log it without notifying the user
          // eslint-disable-next-line no-console
          console.error(err);
        });
    });
  };

  track = (...args: Parameters<AnalyticEventTypes["track"]>) => {
    return this.doIfEnabled(EventTypes.Track, async () => {
      this.externalAnalytics.track(...args);
    });
  };

  triggerPostPurchaseTrackingTokens = (
    basket: Parameters<
      AnalyticEventTypes["triggerPostPurchaseTrackingTokens"]
    >[0],
    invoice: VenueBasedInvoiceSerializer | PublicInvoiceDetailSerializer | null
  ) => {
    if (!this.isEnabled) return;

    const totalPrice = basket?.total_price ?? "0.00";
    const basketEventIds = basket.item_groups?.map((item) => item.event) ?? [];
    const totalQuantity =
      basket.item_groups?.reduce(
        (sum, item) => sum + (item.quantity ?? 0),
        0
      ) ?? 0;

    // Build items array from invoice line items for TradableBits
    const items =
      invoice && "invoice_items" in invoice
        ? invoice?.invoice_items?.map((item) => ({
            itemId: item.id,
            eventAmount: parseFloat(item.price),
            name: item.description,
            quantity: item.quantity,
            variant: item.ticket_type?.name || item.product?.name || undefined,
          })) ?? []
        : [];

    let tokenObj: TrackTokenDataType = {
      fbEvent: "Purchase",
      currency: basket?.currency ?? enums.CURRENCY_MAP.CAD.id,
      venue_id: basket.payment_venue,
      quantity: totalQuantity,
      items: items.length > 0 ? items : undefined,
    };

    if (totalPrice) {
      tokenObj = { ...tokenObj, value: parseInt(totalPrice, 10) };
    }
    // Always use invoice transaction_id
    if (invoice?.transaction_id) {
      tokenObj = { ...tokenObj, transaction_id: invoice.transaction_id };
    }

    this.trackToken(
      enums.TRACKING_TOKEN_LOCATIONS.LOCATION_POST_CHECKOUT.id,
      basket.payment_venue,
      basketEventIds,
      tokenObj
    );
  };

  grantConsent = () => {
    return this.doIfEnabled(EventTypes.GrantConsent, async () => {
      this.externalAnalytics.grantConsent();
    });
  };

  revokeConsent = () => {
    return this.doIfEnabled(EventTypes.RevokeConsent, async () => {
      this.externalAnalytics.revokeConsent();
    });
  };

  viewTicketTypes = (
    item: IHasTicketTypes & IHasName,
    venue: NestedPublicVenueSerializer | PublicVenueSerializer
  ) => {
    if (
      !this.isEnabled ||
      !item.ticket_types ||
      item.ticket_types.length === 0
    ) {
      return;
    }

    this.viewItems(
      item.ticket_types?.map((tt) => {
        const trackingData = gtag.formatTrackingData(
          tt.id,
          item.name,
          tt.name,
          INVOICE_ITEM_TYPE_OPTIONS.INVOICE_ITEM_TYPE_TICKET.id
        );
        return {
          ...tt,
          ...trackingData,
        };
      }) as GtagTicketType[],
      venue
    );
  };

  disableVenue = (venue: typeof this.venue) => {
    return this.doIfEnabled(EventTypes.DisableVenue, async () => {
      if (venue) {
        this.externalAnalytics.disableVenue(venue);
      }
      this.venue = undefined;
    });
  };

  disableAllAnalytics = () => {
    if (!this.isEnabled) return;

    this.externalAnalytics.disableAllAnalytics();
  };
}

export const AnalyticsService = new AnalyticsServiceClass();
